import type { OrderStrategiesEnum } from '@src/tokens/orderStrategy';
import {
  toBig,
  type IOMSExecutionReport4203Markets,
  type JSON_Market,
  type JSON_Security,
  type Order,
} from '@talos/kyoko';
import type Big from 'big.js';
import { isEqual, pick, sortBy } from 'lodash-es';
import parseDuration from 'parse-duration';
import { useMemo, useRef } from 'react';
import { canBackendCalculateTrajectory } from './util';

export type Millis = number;

export function toMillis(str: string | Millis): Millis;
export function toMillis(str?: string | Millis): Millis | undefined;

export function toMillis(str?: string | Millis): Millis | undefined {
  if (!str) {
    return;
  }
  const date = new Date(str);
  date.setMilliseconds(0);
  return date.getTime();
}

/**
 * A paired-down version Market used by the trajectory order chart.
 * This makes it easier to keep it 'stable'.
 */
export interface TrajectoryMarket {
  Market: JSON_Market;
  Symbol: JSON_Security;
}

/**
 * A paired-down version of Order used by the trajectory order chart.
 * It parses and re-maps some fields to make it easier to use in the trajectory calculations.
 *
 * Using this object vs Order also makes it easier to keep 'stable'.
 */
export interface TrajectoryOrder {
  symbol: JSON_Security;
  strategy?: OrderStrategiesEnum;
  orderQty: Big;
  clipSize?: Big;
  clipInterval?: Millis;
  startTime: Millis;
  endTime?: Millis;
  boundsControl?: number;
  markets: TrajectoryMarket[];
  canBackendCalcTrajectory: boolean;
}

type OrderIn = Pick<Order, 'Symbol' | 'OrderQty' | 'Parameters' | 'SubmitTime' | 'Strategy' | 'EndTime'> & {
  Markets: TrajectoryMarket[];
  canBackendCalcTrajectory: boolean;
};

export function getTrajectoryOrder(order: OrderIn): TrajectoryOrder {
  const symbol = order.Symbol;
  const strategy = order.Strategy as OrderStrategiesEnum | undefined;

  const orderQty = toBig(order.OrderQty)!;
  const clipSize = toBig(order.Parameters?.ClipSize);
  const clipInterval = parseDuration(order.Parameters?.ClipInterval) ?? undefined;

  const startTime = toMillis(order.Parameters?.StartTime) ?? toMillis(order.SubmitTime);
  const endTime = toMillis(order.Parameters?.EndTime) ?? toMillis(order.EndTime);
  const boundsControl = order.Parameters?.BoundsControl;

  const markets = order.Markets;
  const canBackendCalcTrajectory = order.canBackendCalcTrajectory;

  return {
    symbol,
    strategy,
    orderQty,
    clipSize,
    clipInterval,
    startTime,
    endTime,
    boundsControl,
    markets,
    canBackendCalcTrajectory,
  };
}

type MarketIn = Pick<Partial<IOMSExecutionReport4203Markets>, 'Market' | 'Symbol'>;

function useStableMarkets(markets: MarketIn[] | undefined): TrajectoryMarket[] {
  const prev = useRef<TrajectoryMarket[]>([]);

  const next = useMemo(
    () =>
      markets && sortBy(markets.map(m => pick(m, ['Market', 'Symbol'])) as TrajectoryMarket[], ['Market', 'Symbol']),
    [markets]
  );

  if (next && !isEqual(prev.current, next)) {
    prev.current = next;
  }
  return prev.current;
}

/**
 * Minimal order fields required for trajectory calculations.
 * Allows using this hook with both full Order objects and partial order data (e.g., from PreTrade).
 */
export type TrajectoryOrderInput = Pick<Order, 'Symbol' | 'OrderQty' | 'SubmitTime' | 'Strategy'> & {
  Parameters?: Order['Parameters'];
  Markets?: MarketIn[];
  EndTime?: string;
  Currency?: Order['Currency'];
};

export function useTrajectoryOrder(order: TrajectoryOrderInput): TrajectoryOrder {
  const { ClipSize, ClipInterval, StartTime, EndTime: ParamEndTime, BoundsControl } = order.Parameters ?? {};

  const Markets = useStableMarkets(order.Markets);
  const canBackendCalcTrajectory = canBackendCalculateTrajectory(order);

  return useMemo(
    () =>
      getTrajectoryOrder({
        Symbol: order.Symbol,
        OrderQty: order.OrderQty,
        Parameters: { ClipSize, ClipInterval, StartTime, EndTime: ParamEndTime, BoundsControl },
        SubmitTime: order.SubmitTime,
        Strategy: order.Strategy,
        Markets: Markets,
        canBackendCalcTrajectory,
        EndTime: order.EndTime,
      }),
    [
      order.Symbol,
      order.OrderQty,
      ClipSize,
      ClipInterval,
      StartTime,
      ParamEndTime,
      BoundsControl,
      order.SubmitTime,
      order.Strategy,
      Markets,
      canBackendCalcTrajectory,
      order.EndTime,
    ]
  );
}
